// TeamLiveSurface.tsx — /live, the LIVE standup board.
//
// Per-teammate cards answering "who's on what, right now" without a meeting. The card's anchor is
// a boxless, actively-typing terminal line (LiveLine); below it, the last-day touchpoints and any
// surfaced blockers. Clicking a card opens the person's live PAGE (/live/:userId —
// TeamLiveMemberSurface). Data comes from useTeamLiveSurface (the route wrapper owns the hook).
// DISTINCT from the retrospective standup (TeamStandupSurface / /standup).
//
// Redesign port 2026-07: prototype live-page.tsx look (PageMast + 3-col card grid) on the real
// rails — every count is computed from the payload (activity window, truncation "N+", D3 empty
// state, You badge) exactly as before.

import type { TeamLiveSurfaceData, TeamLiveMember, TeamLiveItem } from '../../hooks/useTeamLiveSurface';
import { SourceIcon } from '../shared/SourceIcon';
import { relativeTime, formatBriefingDate } from '../../lib/formatting';
import { PageMast, MastTile } from '../shared/workroom';
import { AvatarSq, ActDot, activity, withinDay, byActivity, LiveLine, BlockedCallout, InMeeting, SourcesStrip } from './live-shared';

interface TeamLiveSurfaceProps {
  data: TeamLiveSurfaceData;
  onNav?: (id: string) => void;
  // The signed-in user's id, so their card is badged "You" and accent-styled. In v1 the backend
  // excludes the self card (D3), so this affordance is inert on real data — harmless.
  currentUserId?: string;
}

function TouchpointRow({ item }: { item: TeamLiveItem }) {
  return (
    <p className="flex items-start gap-2 text-[13px] leading-5">
      <span className="mt-0.5 shrink-0"><SourceIcon id={item.site ?? ''} size={14} /></span>
      <span className="min-w-0 flex-1 truncate">{item.title}</span>
      <span className="shrink-0 font-['DM_Mono'] text-[9px] text-muted-foreground">{relativeTime(item.updated_at ?? null)}</span>
    </p>
  );
}

function LiveCard({ member, you, onOpen }: { member: TeamLiveMember; you: boolean; onOpen: () => void }) {
  const act = activity(member.last_active);
  // The board is the LAST DAY view; older touchpoints live on the member page.
  const day = (member.recent ?? []).filter((i) => withinDay(i.updated_at));
  const shown = day.slice(0, 3);
  return (
    <article
      className="flex cursor-pointer flex-col rounded-[24px] border border-border bg-card p-5 text-left shadow-[0_16px_34px_rgba(88,69,42,.055)] transition hover:-translate-y-0.5 hover:shadow-md"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
    >
      <div className="flex items-center gap-3">
        <AvatarSq seed={member.user_id} name={member.display_name} you={you} />
        <div className="min-w-0">
          <p className="text-[15px] font-semibold leading-5">
            {member.display_name}
            {you && <span className="ml-2 rounded-full bg-acc-soft px-2 py-0.5 font-['DM_Mono'] text-[9px] uppercase tracking-[.1em] text-acc">You</span>}
          </p>
          <p className="mt-0.5 text-[12px] text-muted-foreground">{member.display_role}</p>
        </div>
      </div>
      <LiveLine member={member} />
      {member.in_meeting && <div className="mt-2.5"><InMeeting member={member} /></div>}
      {shown.length > 0 && (
        <>
          <p className="mt-4 font-['DM_Mono'] text-[9px] uppercase tracking-[.16em] text-muted-foreground">In the last day</p>
          <div className="mt-1.5 space-y-1.5">
            {shown.map((item) => <TouchpointRow key={item.id} item={item} />)}
          </div>
        </>
      )}
      <BlockedCallout blockers={member.blockers ?? []} />
      <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
        <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <ActDot tone={act.dot} />
          {act.label}
        </span>
        {day.length > 0 && (
          // "in the last day", not "today" — withinDay is a rolling 24h window, and the member page
          // files the same items under calendar-day headers (YESTERDAY). recent_truncated → "50+"
          // (the backend capped the feed; no-silent-caps).
          <span className="font-['DM_Mono'] text-[10px] text-muted-foreground">
            {day.length}{member.recent_truncated ? '+' : ''} in the last day
          </span>
        )}
      </div>
      <SourcesStrip sources={member.sources ?? []} />
    </article>
  );
}

export function TeamLiveSurface({ data, onNav, currentUserId }: TeamLiveSurfaceProps) {
  // Null-safe against the real BFF: a nullish members payload renders the empty state (D3 solo team).
  const members = [...(data.members ?? [])].sort(byActivity);
  const date = data.generated_at ? formatBriefingDate(data.generated_at).toUpperCase() : null;

  if (members.length === 0) {
    return (
      <div className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground">
        <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
          <p className="border-b border-border pb-3 font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">
            Live standup{date ? ` · ${date}` : ''}
          </p>
          <h1 className="mt-7 font-['Instrument_Serif'] text-[clamp(38px,4.4vw,60px)] font-normal leading-[.95] tracking-[-.02em]">
            No one&rsquo;s on the board yet.
          </h1>
          <p className="mt-4 max-w-xl text-[15px] leading-6 text-muted-foreground">
            The standup assembles itself from your team&rsquo;s connected tools. As teammates join and their
            sources sync, their cards show up here, no check-in required.
          </p>
        </div>
      </div>
    );
  }

  const activeToday = members.filter((m) => withinDay(m.last_active)).length;
  const blocked = members.filter((m) => (m.blockers ?? []).length > 0).length;
  const touchpoints = members.reduce(
    (n, m) => n + (m.recent ?? []).filter((i) => withinDay(i.updated_at)).length, 0
  );
  // If any member's feed hit the backend cap, the sum undercounts — show "N+" (no-silent-caps).
  const anyTruncated = members.some((m) => m.recent_truncated);
  // Distinct sources across the board, for the touchpoint tile's note.
  const sourceCount = new Set(members.flatMap((m) => (m.sources ?? []).map((s) => s.site))).size;

  return (
    <div className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground">
      <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
        <PageMast
          eyebrow={`Live standup${date ? ` · ${date}` : ''} · ${members.length} ${members.length === 1 ? 'person' : 'people'}`}
          title={<>Standup, without <em>the meeting.</em></>}
          intro="Assembled live from what everyone is doing across the team’s tools: what each person is on right now, what they touched in the last day, and where they’re blocked."
          tiles={<>
            <MastTile
              label="ACTIVE TODAY"
              value={activeToday}
              note={blocked > 0 ? `${blocked} blocked` : 'no one blocked'}
            />
            <MastTile
              label="TOUCHPOINTS"
              value={`${touchpoints}${anyTruncated ? '+' : ''}`}
              note={`in the last day${sourceCount > 0 ? `, across ${sourceCount} source${sourceCount === 1 ? '' : 's'}` : ''}`}
            />
          </>}
        />
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {members.map((m) => (
            <LiveCard
              key={m.user_id}
              member={m}
              you={m.user_id === currentUserId}
              onOpen={() => onNav?.(`live/${m.user_id}`)}
            />
          ))}
        </section>
      </div>
    </div>
  );
}
